/**
 * API 请求缓存模块
 * 提供通用的接口请求缓存能力，支持多个组件共用同一份缓存数据
 * 核心特性：
 * 1. 数据缓存：已获取的数据直接返回，无需重复请求
 * 2. 请求复用：同一请求在pending状态时复用Promise，防止并发重复请求
 * 3. 缓存管理：支持手动清除指定缓存或全部缓存
 */

import request from './core/request'

/**
 * 缓存数据Map，存储已成功获取的数据
 * Key: 缓存键（API名称+参数序列化）
 * Value: 接口返回的数据
 */
const cacheMap = new Map()

/**
 * 请求Promise Map，存储正在进行中的请求Promise
 * Key: 缓存键（API名称+参数序列化）
 * Value: 请求的Promise对象
 */
const reqPromiseMap = new Map()

/**
 * 生成缓存键
 * @param {string} apiName - API方法名称
 * @param {Array} args - API调用参数数组
 * @returns {string} 唯一的缓存键
 */
function getCacheKey(apiName, args) {
  return `${apiName}_${JSON.stringify(args)}`
}

/**
 * 获取商品分类列表（带缓存）
 * 支持多组件共用缓存，防止重复请求
 * 
 * @param {string|number} expertId - 专家ID
 * @returns {Promise} 返回商品分类列表数据
 * 
 * @example
 * // 组件A调用
 * getGoodsCategoryList('expert001').then(data => console.log(data))
 * 
 * // 组件B同时调用（复用同一个请求）
 * getGoodsCategoryList('expert001').then(data => console.log(data))
 * 
 * // 后续调用（直接返回缓存）
 * getGoodsCategoryList('expert001').then(data => console.log(data))
 */
export function getGoodsCategoryList(expertId) {
  // 生成唯一缓存键
  const cacheKey = getCacheKey('getGoodsCategoryList', [expertId])
  
  // 已有缓存数据，直接返回
  if (cacheMap.has(cacheKey)) {
    return Promise.resolve(cacheMap.get(cacheKey))
  }
  
  // 请求正在进行中，复用pending的Promise，防止并发重复请求
  if (reqPromiseMap.has(cacheKey)) {
    return reqPromiseMap.get(cacheKey)
  }

  // 发起新请求
  const reqPromise = request.get(`/mobile/getGoodsCategoryList/${expertId}`)
    .then(res => {
      // 请求成功，存入缓存
      cacheMap.set(cacheKey, res)
      // 清空pending状态的Promise
      reqPromiseMap.delete(cacheKey)
      return res
    })
    .catch(err => {
      // 请求失败，清空pending状态的Promise
      reqPromiseMap.delete(cacheKey)
      // 重新抛出错误，让调用方处理
      throw err
    })
  
  // 记录pending状态的Promise
  reqPromiseMap.set(cacheKey, reqPromise)
  return reqPromise
}

/**
 * 获取达人配置信息（带缓存）
 * 支持多组件共用缓存，防止重复请求
 * 
 * @returns {Promise} 返回达人配置数据
 * 
 * @example
 * // 组件A调用
 * getExpertConfig().then(res => console.log(res))
 * 
 * // 组件B同时调用（复用同一个请求）
 * getExpertConfig().then(res => console.log(res))
 * 
 * // 后续调用（直接返回缓存）
 * getExpertConfig().then(res => console.log(res))
 */
export function getExpertConfig() {
  // 生成唯一缓存键（无参数）
  const cacheKey = getCacheKey('getExpertConfig', [])
  
  // 已有缓存数据，直接返回
  if (cacheMap.has(cacheKey)) {
    return Promise.resolve(cacheMap.get(cacheKey))
  }
  
  // 请求正在进行中，复用pending的Promise，防止并发重复请求
  if (reqPromiseMap.has(cacheKey)) {
    return reqPromiseMap.get(cacheKey)
  }

  // 发起新请求
  const reqPromise = request.get(`/mobile/getExpertConfig`)
    .then(res => {
      // 请求成功，存入缓存
      cacheMap.set(cacheKey, res)
      // 清空pending状态的Promise
      reqPromiseMap.delete(cacheKey)
      return res
    })
    .catch(err => {
      // 请求失败，清空pending状态的Promise
      reqPromiseMap.delete(cacheKey)
      // 重新抛出错误，让调用方处理
      throw err
    })
  
  // 记录pending状态的Promise
  reqPromiseMap.set(cacheKey, reqPromise)
  return reqPromise
}

/**
 * 获取首页数据（带缓存）
 * 支持多组件共用缓存，防止重复请求
 * 
 * @returns {Promise} 返回首页数据
 * 
 * @example
 * // 组件A调用
 * getHomeData().then(res => console.log(res))
 * 
 * // 组件B同时调用（复用同一个请求）
 * getHomeData().then(res => console.log(res))
 * 
 * // 后续调用（直接返回缓存）
 * getHomeData().then(res => console.log(res))
 */
export function getHomeData() {
  // 生成唯一缓存键（无参数）
  const cacheKey = getCacheKey('getHomeData', [])
  
  // 已有缓存数据，直接返回
  if (cacheMap.has(cacheKey)) {
    return Promise.resolve(cacheMap.get(cacheKey))
  }
  
  // 请求正在进行中，复用pending的Promise，防止并发重复请求
  if (reqPromiseMap.has(cacheKey)) {
    return reqPromiseMap.get(cacheKey)
  }

  // 发起新请求
  const reqPromise = request.get(`/mobile/getHomeData`)
    .then(res => {
      // 请求成功，存入缓存
      cacheMap.set(cacheKey, res)
      // 清空pending状态的Promise
      reqPromiseMap.delete(cacheKey)
      return res
    })
    .catch(err => {
      // 请求失败，清空pending状态的Promise
      reqPromiseMap.delete(cacheKey)
      // 重新抛出错误，让调用方处理
      throw err
    })
  
  // 记录pending状态的Promise
  reqPromiseMap.set(cacheKey, reqPromise)
  return reqPromise
}

/**
 * 清除缓存
 * 
 * @param {string} [apiName] - 可选，API方法名称。不传则清除所有缓存
 * @param {Array} [args] - 可选，API调用参数数组。与apiName配合使用
 * 
 * @example
 * // 清除指定API的指定参数缓存
 * clearCache('getGoodsCategoryList', ['expert001'])
 * 
 * // 清除所有缓存
 * clearCache()
 */
export function clearCache(apiName, args) {
  if (apiName) {
    // 清除指定API的指定参数缓存
    const cacheKey = getCacheKey(apiName, args)
    cacheMap.delete(cacheKey)
    reqPromiseMap.delete(cacheKey)
  } else {
    // 清除所有缓存
    cacheMap.clear()
    reqPromiseMap.clear()
  }
}
